'use client'

import { useMemo } from 'react'
import { usePathname } from '@/i18n/navigation'
import ProductsCategorySlider from './products-category-slider'
import { useAppLocale } from '@/hooks'
import { getLocalizedValue } from '@/lib/utils'
import { CategoryType } from '@/types/category'

/**
 * Resolve the active category (plus its parent chain and siblings) from the current pathname against
 * the category tree — the layout has no access to the route slug, so we derive it on the client.
 */
const deriveActiveCategory = (tree: CategoryType[], slugs: string[], locale: string): CategoryType | undefined => {
  let level = tree
  let node: CategoryType | undefined
  const chain: CategoryType[] = []

  for (const slug of slugs) {
    const found = level.find(category => getLocalizedValue(category, 'slug', locale) === slug)
    if (!found) {
      break
    }
    chain.push(found)
    node = found
    level = found.children ?? []
  }

  if (!node) {
    return undefined
  }

  const parents = chain.slice(0, -1).reverse() // immediate parent first, up to the root
  const parentNode = chain[chain.length - 2]
  const siblings = parentNode?.children ?? tree

  return { ...node, parents, siblings }
}

/**
 * Derive the active category from the current pathname. usePathname strips the locale, so the first
 * segment is the (localized) products route and the rest is the category slug path — dropping the
 * first segment avoids depending on the route-param map (which may not be loaded on first render and
 * would fall back to the untranslated "/products", only matching English).
 */
export const useProductsActiveCategory = (categories: CategoryType[]): CategoryType | undefined => {
  const pathname = usePathname()
  const { backendLocale } = useAppLocale()

  return useMemo(() => {
    const slugs = pathname.split('/').filter(Boolean).slice(1)
    if (slugs.length === 0) {
      return undefined
    }
    return deriveActiveCategory(categories, slugs, backendLocale)
  }, [pathname, categories, backendLocale])
}

type PropsType = {
  categories: CategoryType[]
}

/**
 * Category slider wrapper for the products' layout. Derives the active category from the pathname on
 * the client so the highlight updates instantly on navigation while the slider itself stays mounted.
 */
const ProductsCategoryNav = ({ categories }: PropsType) => {
  const activeCategory = useProductsActiveCategory(categories)

  return <ProductsCategorySlider categories={categories} activeCategory={activeCategory} animate={false} />
}

export default ProductsCategoryNav
